import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multiselect';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';

import { Slider } from '@/components/ui/slider';
import { useActivityTypeFilterOptions } from '@/hooks/useActivityTypesData';
import type { AgeingAnalysisFilters, CustomerAgeingData } from '@/types/ageing-analysis';

interface AdvancedFiltersSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: AgeingAnalysisFilters;
  onFiltersChange: (filters: AgeingAnalysisFilters) => void;
  onApply: () => void;
  onReset: () => void;
  customers: CustomerAgeingData[];
  categoryKeyLabels?: Record<string, string>;
}

export function AdvancedFiltersSheet({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
  onApply,
  onReset,
  customers,
  categoryKeyLabels,
}: Readonly<AdvancedFiltersSheetProps>) {
  const sortKey1Label = categoryKeyLabels?.userSortKey1 ?? 'User Sort Key 1';
  const sortKey2Label = categoryKeyLabels?.userSortKey2 ?? 'User Sort Key 2';
  const sortKey3Label = categoryKeyLabels?.userSortKey3 ?? 'User Sort Key 3';
  const sortKey4Label = categoryKeyLabels?.userSortKey4 ?? 'User Sort Key 4';
  const updateFilter = <K extends keyof AgeingAnalysisFilters>(key: K, value: AgeingAnalysisFilters[K]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const customerOptions = customers.map((customer) => ({
    value: customer.id,
    label: `[${customer.accountNumber}] ${customer.customerName}`,
  }));

  const termsOptions = [...new Set(customers.map((c) => c.terms).filter((t): t is string => Boolean(t)))]
    .sort((a, b) => a.localeCompare(b))
    .map((t) => ({ value: t, label: t }));
  const creditControllerOptions = [
    ...new Set(customers.map((c) => c.creditControllerName).filter((n): n is string => Boolean(n))),
  ]
    .sort((a, b) => a.localeCompare(b))
    .map((n) => ({ value: n, label: n }));
  const accountManagerOptions = [
    ...new Set(customers.map((c) => c.accountManagerName).filter((n): n is string => Boolean(n))),
  ]
    .sort((a, b) => a.localeCompare(b))
    .map((n) => ({ value: n, label: n }));

  const currencyOptions = [...new Set(customers.map((c) => c.currency).filter((v): v is string => Boolean(v)))]
    .sort((a, b) => a.localeCompare(b))
    .map((v) => ({ value: v, label: v }));

  const collectionStrategyOptions = [
    ...new Set(customers.map((c) => c.collectionStrategy).filter((v): v is string => Boolean(v))),
  ]
    .sort((a, b) => a.localeCompare(b))
    .map((v) => ({ value: v, label: v }));

  const { data: lastActionTypeOptions = [] } = useActivityTypeFilterOptions();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md pl-8 flex flex-col overflow-hidden">
        <SheetHeader className="flex-none">
          <SheetTitle>Filters</SheetTitle>
          <SheetDescription>Refine your customer list with advanced filtering options</SheetDescription>
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-y-auto pr-4 mt-2">
          <div className="space-y-4">
            {/* Customer */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Customer</Label>
              <MultiSelect
                options={customerOptions}
                placeholder="Select customers..."
                emptyMessage="No customers found."
                value={filters.customerIds}
                onValueChange={(value) => updateFilter('customerIds', value)}
                maxDisplay={2}
              />
            </div>

            {/* Risk Level */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Risk Level</Label>
              <MultiSelect
                options={[
                  { value: 'critical', label: 'Critical' },
                  { value: 'high', label: 'High' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'low', label: 'Low' },
                  { value: 'none', label: 'None' },
                ]}
                placeholder="Select risk levels..."
                emptyMessage="No risk levels found."
                value={filters.riskLevel}
                onValueChange={(value) => updateFilter('riskLevel', value)}
                maxDisplay={2}
              />
            </div>

            {/* Currency */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Currency</Label>
              <MultiSelect
                options={currencyOptions}
                placeholder="Select currencies..."
                emptyMessage="No currencies found."
                value={filters.currency}
                onValueChange={(value) => updateFilter('currency', value)}
                maxDisplay={3}
              />
            </div>

            {/* Terms */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Terms</Label>
              <MultiSelect
                options={termsOptions}
                placeholder="Select terms..."
                emptyMessage="No terms found."
                value={filters.terms}
                onValueChange={(value) => updateFilter('terms', value)}
                maxDisplay={3}
              />
            </div>

            {/* Credit Controller */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Credit Controller</Label>
              <MultiSelect
                options={creditControllerOptions}
                placeholder="Select credit controllers..."
                emptyMessage="No credit controllers found."
                value={filters.creditController}
                onValueChange={(value) => updateFilter('creditController', value)}
                maxDisplay={2}
              />
            </div>

            {/* Account Manager */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Account Manager</Label>
              <MultiSelect
                options={accountManagerOptions}
                placeholder="Select account managers..."
                emptyMessage="No account managers found."
                value={filters.accountManager}
                onValueChange={(value) => updateFilter('accountManager', value)}
                maxDisplay={2}
              />
            </div>

            {/* Credit Limit Range */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Credit Limit Range</Label>
              <div className="space-y-3">
                <div className="px-2">
                  <Slider
                    min={0}
                    max={1000000}
                    step={10000}
                    value={[filters.creditLimitMin ?? 0, filters.creditLimitMax ?? 1000000]}
                    onValueChange={([min, max]) =>
                      onFiltersChange({ ...filters, creditLimitMin: min, creditLimitMax: max })
                    }
                    className="w-full"
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{formatCurrency(filters.creditLimitMin ?? 0)}</span>
                  <span>{formatCurrency(filters.creditLimitMax ?? 1000000)}</span>
                </div>
              </div>
            </div>

            {/* Last Payment Date Range */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Last Payment Date Range</Label>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">From</Label>
                  <Input
                    type="date"
                    value={filters.lastPaymentFrom ?? ''}
                    onChange={(e) => updateFilter('lastPaymentFrom', e.target.value || null)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">To</Label>
                  <Input
                    type="date"
                    value={filters.lastPaymentTo ?? ''}
                    onChange={(e) => updateFilter('lastPaymentTo', e.target.value || null)}
                  />
                </div>
              </div>
            </div>

            {/* Collection Strategy */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Collection Strategy</Label>
              <MultiSelect
                options={collectionStrategyOptions}
                placeholder="Select collection strategies..."
                emptyMessage="No collection strategies found."
                value={filters.collectionStrategy}
                onValueChange={(value) => updateFilter('collectionStrategy', value)}
                maxDisplay={2}
              />
            </div>

            {/* Last Action Type */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Last Action Type</Label>
              <MultiSelect
                options={lastActionTypeOptions}
                placeholder="Select action types..."
                emptyMessage="No action types found."
                value={filters.lastActionTypes}
                onValueChange={(value) => updateFilter('lastActionTypes', value)}
                maxDisplay={2}
              />
            </div>

            {/* Days Overdue Range */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Days Overdue Range</Label>
              <div className="space-y-3">
                <div className="px-2">
                  <Slider
                    min={0}
                    max={180}
                    step={10}
                    value={[filters.daysOverdueMin ?? 0, filters.daysOverdueMax ?? 180]}
                    onValueChange={([min, max]) =>
                      onFiltersChange({ ...filters, daysOverdueMin: min, daysOverdueMax: max })
                    }
                    className="w-full"
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{filters.daysOverdueMin ?? 0} days</span>
                  <span>{filters.daysOverdueMax ?? 180} days</span>
                </div>
              </div>
            </div>

            {/* Outstanding Amount Range */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Outstanding Amount Range</Label>
              <div className="space-y-3">
                <div className="px-2">
                  <Slider
                    min={0}
                    max={500000}
                    step={10000}
                    value={[filters.outstandingMin ?? 0, filters.outstandingMax ?? 500000]}
                    onValueChange={([min, max]) =>
                      onFiltersChange({ ...filters, outstandingMin: min, outstandingMax: max })
                    }
                    className="w-full"
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{formatCurrency(filters.outstandingMin ?? 0)}</span>
                  <span>{formatCurrency(filters.outstandingMax ?? 500000)}</span>
                </div>
              </div>
            </div>

            {/* User Sort Keys */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Category Keys</Label>
              <div className="space-y-2">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{sortKey1Label}</Label>
                  <MultiSelect
                    options={[...new Set(customers.map((c) => c.userSortKey1).filter((v): v is string => Boolean(v)))]
                      .sort((a, b) => a.localeCompare(b))
                      .map((v) => ({ value: v, label: v }))}
                    placeholder={`Select ${sortKey1Label.toLowerCase()} values...`}
                    emptyMessage="No values found."
                    value={filters.userSortKey1}
                    onValueChange={(value) => updateFilter('userSortKey1', value)}
                    maxDisplay={2}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{sortKey2Label}</Label>
                  <MultiSelect
                    options={[...new Set(customers.map((c) => c.userSortKey2).filter((v): v is string => Boolean(v)))]
                      .sort((a, b) => a.localeCompare(b))
                      .map((v) => ({ value: v, label: v }))}
                    placeholder={`Select ${sortKey2Label.toLowerCase()} values...`}
                    emptyMessage="No values found."
                    value={filters.userSortKey2}
                    onValueChange={(value) => updateFilter('userSortKey2', value)}
                    maxDisplay={2}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{sortKey3Label}</Label>
                  <MultiSelect
                    options={[...new Set(customers.map((c) => c.userSortKey3).filter((v): v is string => Boolean(v)))]
                      .sort((a, b) => a.localeCompare(b))
                      .map((v) => ({ value: v, label: v }))}
                    placeholder={`Select ${sortKey3Label.toLowerCase()} values...`}
                    emptyMessage="No values found."
                    value={filters.userSortKey3}
                    onValueChange={(value) => updateFilter('userSortKey3', value)}
                    maxDisplay={2}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{sortKey4Label}</Label>
                  <MultiSelect
                    options={[...new Set(customers.map((c) => c.userSortKey4).filter((v): v is string => Boolean(v)))]
                      .sort((a, b) => a.localeCompare(b))
                      .map((v) => ({ value: v, label: v }))}
                    placeholder={`Select ${sortKey4Label.toLowerCase()} values...`}
                    emptyMessage="No values found."
                    value={filters.userSortKey4}
                    onValueChange={(value) => updateFilter('userSortKey4', value)}
                    maxDisplay={2}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <SheetFooter className="flex-none mt-4 pt-4 border-t">
          <Button variant="outline" onClick={onReset}>
            Reset
          </Button>
          <Button onClick={onApply}>Apply Filters</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
